import { useState } from 'react'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslate } from '@repo/unicity'
import { useCurrentLanguage } from '@/core/utils'

export type MoneyBackGuaranteeData = {
    isEnable?: boolean
    headerImage?: string
    buttonText?: { english?: string; native?: string }
    titles?: { english?: string[]; native?: string[] }
    subTitles?: { english?: string[]; native?: string[] }
    paragraphs?: { english?: string[]; native?: string[] }
}

export interface MoneyBackSectionProps {
    data?: MoneyBackGuaranteeData
}

export const MoneyBackSection = ({ data }: MoneyBackSectionProps) => {
    const { translate } = useTranslate()
    const language = useCurrentLanguage()
    const [open, setOpen] = useState(false)

    if (data && data.isEnable === false) return null

    const pickArr = (field?: { english?: string[]; native?: string[] }): string[] => {
        if (!field) return []
        if (language === 'EN') return field.english ?? field.native ?? []
        return field.native ?? field.english ?? []
    }

    const linkText = translate('our_money_back_guarantee')
    const titleArr = pickArr(data?.titles)
    const subTitleArr = pickArr(data?.subTitles)
    const paragraphArr = pickArr(data?.paragraphs)

    return (
        <>
            <Stack spacing={1.5} alignItems="center" data-testid="money-back-section-stack-typography">
                <Typography
                    variant="body2"
                    onClick={() => setOpen(true)}
                    sx={{ fontSize: 14, color: '#446081', textDecoration: 'underline', cursor: 'pointer' }}
                    data-testid="money-back-section-typography-1">
                    {linkText}
                </Typography>
            </Stack>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth data-testid="money-back-section-dialog">
                <DialogContent sx={{ p: 3 }} data-testid="money-back-section-dialog-content">
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} data-testid="money-back-section-stack-icon-button">
                        {data?.headerImage ? (
                            <Box component="img" src={data.headerImage} alt="" sx={{ width: 60, height: 'auto' }} data-testid="money-back-section-box-1" />
                        ) : (
                            <Box data-testid="money-back-section-box-2" />
                        )}
                        <IconButton onClick={() => setOpen(false)} size="small" data-testid="money-back-section-icon-button">
                            <Box
                                component="svg"
                                viewBox="0 0 24 24"
                                sx={{ width: 20, height: 20, fill: 'currentColor' }}
                                data-testid="money-back-section-box-path">
                                <path
                                    d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                                    data-testid="money-back-section-path"
                                />
                            </Box>
                        </IconButton>
                    </Stack>
                    <Stack spacing={2} alignItems="flex-start" pb={2} data-testid="money-back-section-stack-typography-close">
                        {titleArr.map((t, i) => (
                            <Typography
                                key={i}
                                textAlign="left"
                                sx={{ fontSize: 20, fontWeight: 700, color: '#003764' }}
                                data-testid="money-back-section-typography-2">
                                {t}
                            </Typography>
                        ))}
                        {subTitleArr.map((s, i) => (
                            <Typography
                                key={i}
                                textAlign="left"
                                sx={{ fontSize: 18, fontWeight: 600, color: '#003764' }}
                                data-testid="money-back-section-typography-3">
                                {s}
                            </Typography>
                        ))}
                        {paragraphArr.map((p, i) => (
                            <Typography
                                variant="body2"
                                key={i}
                                dangerouslySetInnerHTML={{ __html: p }}
                                sx={{ fontSize: 12, fontWeight: 400, lineHeight: 1.7, color: '#446081', whiteSpace: 'pre-line' }}
                                data-testid="money-back-section-typography-4"
                            />
                        ))}
                        <Typography
                            variant="body2"
                            onClick={() => setOpen(false)}
                            sx={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: '#003764',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textTransform: 'uppercase',
                                mt: '40px !important'
                            }}
                            data-testid="money-back-section-typography-close">
                            {translate('close')}
                        </Typography>
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    )
}
